/**
 * Санитизация и парсинг текста argumentSelectionLogic.
 * Поддерживаются только теги выделения: <plus>...</plus> и <minus>...</minus>.
 * Все остальные теги удаляются.
 */

export type ArgumentSelectionSegment =
  | { type: 'text'; content: string }
  | { type: 'plus'; content: string }
  | { type: 'minus'; content: string };

function isAllowedTag(tagContent: string): boolean {
  const t = tagContent.trim().toLowerCase();
  return t === 'plus' || t === '/plus' || t === 'minus' || t === '/minus';
}

/**
 * Удаляет из текста все теги, кроме <plus>, </plus>, <minus>, </minus>.
 */
export function sanitizeArgumentSelectionLogic(text: string): string {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/<([^>]+)>/g, (_match, tagContent: string) =>
    isAllowedTag(tagContent) ? _match : ''
  );
}

const RE_OPEN_PLUS = /<plus\s*>/gi;
const RE_CLOSE_PLUS = /<\/plus\s*>/gi;
const RE_OPEN_MINUS = /<minus\s*>/gi;
const RE_CLOSE_MINUS = /<\/minus\s*>/gi;

/**
 * Разбивает санитизированный текст на сегменты: обычный текст и фрагменты внутри <plus>...</plus> и <minus>...</minus>.
 */
export function parseArgumentSelectionLogic(text: string): ArgumentSelectionSegment[] {
  const sanitized = sanitizeArgumentSelectionLogic(text);
  const segments: ArgumentSelectionSegment[] = [];
  let pos = 0;

  while (pos < sanitized.length) {
    RE_OPEN_PLUS.lastIndex = pos;
    RE_OPEN_MINUS.lastIndex = pos;
    const mPlus = RE_OPEN_PLUS.exec(sanitized);
    const mMinus = RE_OPEN_MINUS.exec(sanitized);
    const startPlus = mPlus ? mPlus.index : Infinity;
    const startMinus = mMinus ? mMinus.index : Infinity;

    if (startPlus === Infinity && startMinus === Infinity) {
      segments.push({ type: 'text', content: sanitized.slice(pos) });
      break;
    }

    if (startPlus < startMinus) {
      if (startPlus > pos) {
        segments.push({ type: 'text', content: sanitized.slice(pos, startPlus) });
      }
      const contentStart = startPlus + (mPlus ? mPlus[0].length : 0);
      RE_CLOSE_PLUS.lastIndex = contentStart;
      const closePlus = RE_CLOSE_PLUS.exec(sanitized);
      const closeEnd = closePlus ? closePlus.index + closePlus[0].length : sanitized.length;
      const content = sanitized.slice(contentStart, closePlus ? closePlus.index : undefined);
      segments.push({ type: 'plus', content });
      pos = closeEnd;
      continue;
    }

    if (startMinus > pos) {
      segments.push({ type: 'text', content: sanitized.slice(pos, startMinus) });
    }
    const contentStart = startMinus + (mMinus ? mMinus[0].length : 0);
    RE_CLOSE_MINUS.lastIndex = contentStart;
    const closeMinus = RE_CLOSE_MINUS.exec(sanitized);
    const closeEnd = closeMinus ? closeMinus.index + closeMinus[0].length : sanitized.length;
    const content = sanitized.slice(contentStart, closeMinus ? closeMinus.index : undefined);
    segments.push({ type: 'minus', content });
    pos = closeEnd;
  }

  return segments;
}
